/**
 * Supabase Configuration
 * Automatically selects the correct Supabase project based on environment
 */

export interface SupabaseConfig {
  url: string;
  anonKey: string;
  serviceRoleKey?: string;
  dbUrl?: string;
}

// Production credentials (hardcoded as fallback)
const PRODUCTION_PROJECT_ID = "eirnvzhyhfinzpcqywjt";
const PRODUCTION_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVpcm52emh5aGZpbnpwY3F5d2p0Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjA5MzMxMTksImV4cCI6MjA3NjUwOTExOX0.xSSD6VXl5NRlVkM1Vkca103nBHq46D-s6L5BG4M9Tp8";

// Demo credentials
const DEMO_PROJECT_ID = "pturcacflihinxoiphte";
const DEMO_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InB0dXJjYWNmbGloaW54b2lwaHRlIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjIzNTkyMzMsImV4cCI6MjA3NzkzNTIzM30.5x-iy2Prq2UR8iHXv4PS9d_VluRBz9fHh_E4sQ_Ut-4";

/**
 * Detect if we're in demo environment
 */
export function isDemoEnvironment(): boolean {
  if (typeof window === 'undefined') {
    // Server-side: check environment variable
    return Deno?.env?.get('ENVIRONMENT') === 'demo';
  }
  // Client-side: check hostname for demo tenant subdomains or staging
  const hostname = window.location.hostname;
  return hostname.endsWith('-demo.pathkit.pro') || hostname === 'staging.pathkit.pro' || hostname.includes('figmaiframepreview');
}

/**
 * Get the appropriate Supabase configuration based on environment
 */
export function getSupabaseConfig(): SupabaseConfig {
  const isDemo = isDemoEnvironment();
  
  if (typeof window === 'undefined') {
    // Server-side (Deno)
    if (isDemo) {
      const demoProjectId = Deno.env.get('DEMO_SUPABASE_PROJECT_ID') || DEMO_PROJECT_ID;
      return {
        url: Deno.env.get('DEMO_SUPABASE_URL') || `https://${demoProjectId}.supabase.co`,
        anonKey: Deno.env.get('DEMO_SUPABASE_ANON_KEY') || DEMO_ANON_KEY,
        serviceRoleKey: Deno.env.get('DEMO_SUPABASE_SERVICE_ROLE_KEY') || '',
        dbUrl: Deno.env.get('DEMO_SUPABASE_DB_URL') || '',
      };
    } else {
      const prodProjectId = Deno.env.get('SUPABASE_PROJECT_ID') || PRODUCTION_PROJECT_ID;
      return {
        url: Deno.env.get('SUPABASE_URL') || `https://${prodProjectId}.supabase.co`,
        anonKey: Deno.env.get('SUPABASE_ANON_KEY') || PRODUCTION_ANON_KEY,
        serviceRoleKey: Deno.env.get('SUPABASE_SERVICE_ROLE_KEY') || '',
        dbUrl: Deno.env.get('SUPABASE_DB_URL') || '',
      };
    }
  } else {
    // Client-side (Browser)
    if (isDemo) {
      const demoProjectId = (window as any).__DEMO_SUPABASE_PROJECT_ID__ || DEMO_PROJECT_ID;
      return {
        url: (window as any).__DEMO_SUPABASE_URL__ || `https://${demoProjectId}.supabase.co`,
        anonKey: (window as any).__DEMO_SUPABASE_ANON_KEY__ || DEMO_ANON_KEY,
      };
    } else {
      const prodProjectId = (window as any).__SUPABASE_PROJECT_ID__ || PRODUCTION_PROJECT_ID;
      return {
        url: (window as any).__SUPABASE_URL__ || `https://${prodProjectId}.supabase.co`,
        anonKey: (window as any).__SUPABASE_ANON_KEY__ || PRODUCTION_ANON_KEY,
      };
    }
  }
}

/**
 * Get project ID from Supabase URL
 */
export function getProjectId(): string {
  const config = getSupabaseConfig();
  const match = config.url.match(/https:\/\/([^.]+)\.supabase\.co/);
  return match ? match[1] : '';
}

/**
 * Get public anon key
 */
export function getPublicAnonKey(): string {
  return getSupabaseConfig().anonKey;
}